import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Papa from 'papaparse';
import _ from 'lodash';

// Define the shape of a candidate
interface Candidate {
  'Submission ID': string;
  'Respondent ID': string;
  'Submitted at': string;
  'Nome Completo': string;
  'E-mail': string;
  'Telefone': string;
  'LinkedIn': string;
  'GitHub': string;
  'Possui indicação? Poe o LinkedIn da pessoa aqui 👇': string;
  'Já conhecia a waffle/the news? ☕️': string;
  'Quanto tempo de experiência você tem com desenvolvimento web?': string;
  'Já trabalhou com projetos open-source ou pessoais? Compartilhe links relevantes.': string;
  'Tem experiência com newsletters ou publicação de conteúdo digital?': string;
  'Quais desafios você já enfrentou ao consumir e integrar APIs?': string;
  'Qual seu nível de inglês?': string;
  'Já trabalhou em equipe remota? Como foi essa experiência?': string;
  'Dê um exemplo de um problema complexo que você resolveu e como fez isso.': string;
  'Qual sua disponibilidade para começar?': string;
  'Está cursando faculdade? Se sim, qual curso e semestre?': string;
  'Qual sua pretensão salarial?': number;
  'Quais são suas expectativas de crescimento nessa vaga?': string;
  'Algo mais?': string;
  '6. Carregue seu currículo aqui 👇': string;
  // Technology-specific columns
  'Quais tecnologias você domina? (TypeScript)': boolean;
  'Quais tecnologias você domina? (ReactJs)': boolean;
  'Quais tecnologias você domina? (MySQL)': boolean;
  'Quais tecnologias você domina? (Serverless (CF Worker ou Lambda))': boolean;
  'Quais tecnologias você domina? (Tailwind)': boolean;
  'Quais tecnologias você domina? (HTML e CSS)': boolean;
  'Quais tecnologias você domina? (GitHub)': boolean;
  'Quais tecnologias você domina? (APIs REST)': boolean;
  'Quais tecnologias você domina? (Python)': boolean;
  'Quais tecnologias você domina? (Docker)': boolean;
  'Quais tecnologias você domina? (Criação e/ou manipulação de agentes de AI (Gemini, OpenAI))': boolean;
  dominatedTechnologies: string[];
}

// Extend Window interface to include fs
declare global {
  interface Window {
    fs?: {
      readFile: (path: string, options?: { encoding?: string }) => Promise<Uint8Array | string>;
    };
  }
}

export function FullStackCandidatesDashboard() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [technologyFilter, _setTechnologyFilter] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Novos estados para paginação e ordenação
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState('Submitted at');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  
  const itemsPerPage = 50;

  // Nova função para carregar CSV de um caminho específico
  const loadCandidatesFromFile = async (filePath: string) => {
    try {
      // Verifica se estamos em um ambiente de navegador ou Node.js
      const fileContent = typeof window !== 'undefined' && window.fs
        ? await window.fs.readFile(filePath, { encoding: 'utf-8' })
        : await fetch(filePath).then(response => response.text());

      Papa.parse(fileContent as string, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const processedCandidates = (results.data as Candidate[]).map(candidate => ({
            ...candidate,
            dominatedTechnologies: [
              candidate['Quais tecnologias você domina? (TypeScript)'] && 'TypeScript',
              candidate['Quais tecnologias você domina? (ReactJs)'] && 'ReactJs',
              candidate['Quais tecnologias você domina? (MySQL)'] && 'MySQL',
              candidate['Quais tecnologias você domina? (Serverless (CF Worker ou Lambda))'] && 'Serverless',
              candidate['Quais tecnologias você domina? (Tailwind)'] && 'Tailwind',
              candidate['Quais tecnologias você domina? (HTML e CSS)'] && 'HTML e CSS',
              candidate['Quais tecnologias você domina? (GitHub)'] && 'GitHub',
              candidate['Quais tecnologias você domina? (APIs REST)'] && 'APIs REST',
              candidate['Quais tecnologias você domina? (Python)'] && 'Python',
              candidate['Quais tecnologias você domina? (Docker)'] && 'Docker',
              candidate['Quais tecnologias você domina? (Criação e/ou manipulação de agentes de AI (Gemini, OpenAI))'] && 'AI Agents'
            ].filter(Boolean) as string[]
          }));
          
          setCandidates(processedCandidates);
          setFilteredCandidates(processedCandidates);
        }
      });
    } catch (error) {
      console.error('Erro ao carregar arquivo:', error);
      alert('Não foi possível carregar o arquivo de candidatos.');
    }
  };

  // Carregar candidatos ao montar o componente
  useEffect(() => {
    const defaultCsvPath = '/Desenvolvedor_Full_Stack_Jr_Front-end_Specialist_Submissions_2025-02-10.csv';
    loadCandidatesFromFile(defaultCsvPath);
  }, []);

  // Modificar a função de upload de arquivo para usar a mesma lógica de processamento
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const processedCandidates = (results.data as Candidate[]).map(candidate => ({
            ...candidate,
            dominatedTechnologies: [
              candidate['Quais tecnologias você domina? (TypeScript)'] && 'TypeScript',
              candidate['Quais tecnologias você domina? (ReactJs)'] && 'ReactJs',
              candidate['Quais tecnologias você domina? (MySQL)'] && 'MySQL',
              candidate['Quais tecnologias você domina? (Serverless (CF Worker ou Lambda))'] && 'Serverless',
              candidate['Quais tecnologias você domina? (Tailwind)'] && 'Tailwind',
              candidate['Quais tecnologias você domina? (HTML e CSS)'] && 'HTML e CSS',
              candidate['Quais tecnologias você domina? (GitHub)'] && 'GitHub',
              candidate['Quais tecnologias você domina? (APIs REST)'] && 'APIs REST',
              candidate['Quais tecnologias você domina? (Python)'] && 'Python',
              candidate['Quais tecnologias você domina? (Docker)'] && 'Docker',
              candidate['Quais tecnologias você domina? (Criação e/ou manipulação de agentes de AI (Gemini, OpenAI))'] && 'AI Agents'
            ].filter(Boolean) as string[]
          }));
          
          setCandidates(processedCandidates);
          setFilteredCandidates(processedCandidates);
        }
      });
    };
    reader.readAsText(file);
  };

  // Filter candidates
  useEffect(() => {
    let result = candidates;

    // Search filter
    if (searchTerm) {
      result = result.filter(candidate => 
        candidate['Nome Completo']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate['E-mail']?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Technology filter
    if (technologyFilter) {
      result = result.filter(candidate => 
        candidate.dominatedTechnologies?.includes(technologyFilter)
      );
    }

    setFilteredCandidates(result);
  }, [searchTerm, technologyFilter, candidates]);

  // Modificar a função de ordenação para suportar data
  const handleSort = (field: string) => {
    setOrderBy(field);
    setOrderDirection(current => current === 'asc' ? 'desc' : 'asc');
  };

  // Atualizar a lógica de paginação para suportar diferentes tipos de ordenação
  const paginatedCandidates = filteredCandidates
    .sort((a, b) => {
      const aValue = a[orderBy as keyof Candidate];
      const bValue = b[orderBy as keyof Candidate];

      // Tratamento especial para o campo de data
      if (orderBy === 'Submitted at') {
        const dateA = new Date(aValue as string);
        const dateB = new Date(bValue as string);
        return orderDirection === 'asc' 
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      // Lógica de ordenação padrão para outros campos
      return orderDirection === 'asc' 
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    })
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);

  // Função para converter links em elementos clicáveis
  const renderWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-500 hover:text-pink-600 underline"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className=" min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
              Candidatos Full Stack Jr
            </h1>
            <Input 
              type="file" 
              accept=".csv"
              onChange={handleFileUpload}
              className="w-64 "
            />
          </div>
          <div className="flex space-x-4">
            <Input 
              placeholder="Buscar candidato" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 h-10"
            />
          </div>
        </div>

        {/* Statistics Cards com design atualizado para cor rosa da Waffle */}
        <div className="grid grid-cols-4 gap-4 mb-6 mt-4">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-pink-600 to-pink-400">
              <CardTitle className="text-white">Total de Candidatos</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-black dark:text-white mt-4">{candidates.length}</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-pink-600 to-pink-400">
              <CardTitle className="text-white">TypeScript</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold mt-4">
                {candidates.filter(c => c.dominatedTechnologies?.includes('TypeScript')).length}
              </p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-pink-600 to-pink-400">
              <CardTitle className="text-white">React</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold mt-4">
                {candidates.filter(c => c.dominatedTechnologies?.includes('ReactJs')).length}
              </p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-pink-600 to-pink-400">
              <CardTitle className="text-white">Média Salarial</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold mt-4">
                R$ {_.meanBy(candidates, 'Qual sua pretensão salarial?')?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Ordenação */}
        <div className="mb-4 flex justify-end">
          <Select 
            value={orderBy}
            onValueChange={(value) => handleSort(value)}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Submitted at">Data de Submissão</SelectItem>
              <SelectItem value="Nome Completo">Nome</SelectItem>
              <SelectItem value="Qual sua pretensão salarial?">Pretensão Salarial</SelectItem>
              <SelectItem value="Quanto tempo de experiência você tem com desenvolvimento web?">Experiência</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Candidates Table */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Candidatos</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tecnologias</TableHead>
                  <TableHead>Experiência Web</TableHead>
                  <TableHead>Pretensão Salarial</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCandidates.map((candidate, index) => (
                  <TableRow 
                    key={index}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      setSelectedCandidate(candidate);
                      setIsModalOpen(true);
                    }}
                  >
                    <TableCell>{candidate['Nome Completo']}</TableCell>
                    <TableCell>{candidate['E-mail']}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {candidate.dominatedTechnologies?.map(tech => (
                          <span key={tech} className="bg-gray-100 text-xs px-2 py-1 rounded dark:bg-gray-700 dark:text-white">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{candidate['Quanto tempo de experiência você tem com desenvolvimento web?']}</TableCell>
                    <TableCell>
                      R$ {candidate['Qual sua pretensão salarial?']?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <a
                        href={candidate['6. Carregue seu currículo aqui 👇']}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-pink-500 hover:bg-pink-600 text-white px-3 py-1 rounded text-sm inline-block"
                      >
                        Download CV
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Paginação */}
        <div className="mt-4 flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="px-4 py-1">
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
          >
            Próximo
          </button>
        </div>

        {/* Modal com scroll e links clicáveis */}
        {isModalOpen && selectedCandidate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full h-[90vh] flex flex-col p-6 shadow-xl">
              <div className="sticky top-0 bg-white pb-4 border-b mb-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {selectedCandidate['Nome Completo']}
                  </h2>
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-gray-100"
                  >
                    ✕
                  </button>
                </div>
                <div className="mt-2 flex gap-2">
                  <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">
                    {selectedCandidate['E-mail']}
                  </span>
                  <a
                    href={selectedCandidate['6. Carregue seu currículo aqui 👇']}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="bg-pink-500 hover:bg-pink-600 text-white px-3 py-1 rounded-full text-sm"
                  >
                    Download CV
                  </a>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col gap-4">
                  {Object.entries(selectedCandidate).map(([key, value]) => {
                    if (key.startsWith('Quais tecnologias você domina? (') || key === 'dominatedTechnologies') {
                      return null;
                    }
                    
                    return (
                      <div key={key} className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-700 mb-2">{key}</h3>
                        <p className="text-gray-600 break-words">
                          {Array.isArray(value) 
                            ? value.join(', ')
                            : typeof value === 'number'
                              ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                              : renderWithLinks(String(value || '-'))}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}